import React, { useEffect, useState } from 'react'

import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'

import { apiGetLeaderboard, apiGetPointsBreakdown } from '@src/api/point.api'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { CheckPoints } from '@src/pages/Points/CheckPoints'
import { Leaderboard } from '@src/pages/Points/Leaderboard'
import { Rules } from '@src/pages/Points/Rules'
import { ILeaderboardDataResponse } from '@src/types/point.api'
import { logger } from '@src/utils/logger'

type Props = {}

const TAB_NAMES = {
    CHECK_MILES: 'Check Miles',
    RULES: 'Rules',
    LEADERBOARD: 'Leaderboard',
}
const tabs = [TAB_NAMES.CHECK_MILES, TAB_NAMES.RULES, TAB_NAMES.LEADERBOARD]

const PointsPage: React.FC<Props> = () => {
    const { evmWalletAddress } = useWalletConnections()
    const [loading, setLoading] = useState<boolean>(false)
    const [leaderboardData, setLeaderboardData] = useState<ILeaderboardDataResponse[]>([])
    const [selectedTab, setSelectedTab] = useState<string>(tabs[0])

    // Initialize points from localStorage if available
    const [points, setPoints] = useState(() => {
        const savedPoints = localStorage.getItem('points')
        return savedPoints ? parseInt(savedPoints) : 0
    })

    const [breakdown, setBreakdown] = useState<any>(null)

    const fetchPoints = async () => {
        if (!evmWalletAddress) return

        try {
            // Get the breakdown
            const breakdownData = await apiGetPointsBreakdown(evmWalletAddress)

            // Convert breakdownData into a chart format
            const formattedBreakdown = Object.entries(breakdownData).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { amount: value },
                }),
                {}
            )
            setBreakdown(formattedBreakdown)

            // Sum all numeric values in the breakdown object
            const pointsTotal = Object.values(breakdownData)
                .filter(value => typeof value === 'number')
                .reduce((sum, value) => sum + value, 0)
            setPoints(pointsTotal)

            // Save the points to localStorage
            try {
                localStorage.setItem('points', pointsTotal.toString())
            } catch (error) {
                logger.error('Error saving points to localStorage:', { error })
            }
        } catch (error) {
            logger.error('Error fetching points:', { error })
            setPoints(0)
            setBreakdown(null)
        }
    }

    const fetchLeaderboard = async () => {
        setLoading(true)
        try {
            const data: ILeaderboardDataResponse[] = await apiGetLeaderboard()
            const sortedData = data.sort((a, b) => b.total_user_points - a.total_user_points)
            const rankedData = sortedData.map((item, index) => ({
                ...item,
                rank: index + 1,
            }))
            setLeaderboardData(rankedData)
        } catch (error) {
            logger.error('Error fetching leaderboard:', { error })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchPoints()
    }, [evmWalletAddress])

    useEffect(() => {
        if (selectedTab === TAB_NAMES.LEADERBOARD && !leaderboardData.length) {
            fetchLeaderboard()
        }
    }, [selectedTab])

    return (
        <>
            <div className='mx-auto mb-5 flex space-x-5'>
                {tabs.map(tab => (
                    <motion.div
                        key={`tab-${tab}`}
                        className={cn('cursor-pointer')}
                        onClick={() => setSelectedTab(tab)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <p
                            className={cn(
                                'p-2 transition-colors duration-200',
                                tab === selectedTab ? 'text-[#85C3F8]' : 'text-[#B6B2BA]'
                            )}
                        >
                            {tab}
                        </p>
                        <motion.div
                            className='h-[2px] w-full bg-[#85C3F8]'
                            initial={false}
                            animate={{
                                opacity: tab === selectedTab ? 1 : 0,
                                scale: tab === selectedTab ? 1 : 0.8,
                            }}
                            transition={{ duration: 0.2 }}
                        />
                    </motion.div>
                ))}
            </div>

            <AnimatePresence mode='wait'>
                <motion.div
                    key={selectedTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                >
                    {selectedTab === TAB_NAMES.CHECK_MILES && (
                        <CheckPoints
                            address={evmWalletAddress as `0x${string}`}
                            points={points}
                            breakdown={breakdown}
                        />
                    )}
                    {selectedTab === TAB_NAMES.RULES && <Rules />}
                    {selectedTab === TAB_NAMES.LEADERBOARD && (
                        <Leaderboard account={evmWalletAddress} loading={loading} leaderboard={leaderboardData} />
                    )}
                </motion.div>
            </AnimatePresence>
        </>
    )
}

export default PointsPage
